<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import { storeToRefs } from 'pinia';
import { useOptionsStore } from '@/stores/options';
import { useFoodflowSessionStore } from '@/stores/foodflowSession';
import { categoryIcon } from '@/utils/foodCategoryIcons';
import { useDateFormatter } from '@/composables/dateFormatter';
import { trans } from 'laravel-vue-i18n';
import FoodFlowBadge from '@/components/FoodFlow/FoodFlowBadge.vue';
import ScaleConnectionStatus from '@/components/FoodFlow/ScaleConnectionStatus.vue';
import { useScaleAutoFill } from '@/composables/scaleReader';

const props = defineProps({
    sessionHash: {
        type: String,
        required: true
    }
});

const emit = defineEmits(['close']);

const { foodCategoryOptions } = storeToRefs(useOptionsStore());
const foodflowSessionStore = useFoodflowSessionStore();
const { addEntry, removeEntry } = foodflowSessionStore;
const { formatDateTime } = useDateFormatter();

const session = ref(null);
const loading = ref(true);
const selectedCategoryId = ref(null);
const weight = ref('');
const submitting = ref(false);
const weightInput = ref(null);

const { isSettling } = useScaleAutoFill(weight);

const focusWeightInput = () => {
    nextTick(() => weightInput.value?.$el?.querySelector('input')?.focus());
};

const isIntake = computed(() => session.value?.type === 'intake');
const weightSign = computed(() => isIntake.value ? '+' : '-');
const weightColorClass = computed(() => isIntake.value ? 'text-[#2d6b1a]' : 'text-[#a83430]');
const totalWeight = computed(() => (session.value?.entries ?? []).reduce((sum, e) => sum + parseFloat(e.weight), 0));
const canAdd = computed(() => selectedCategoryId.value && parseFloat(weight.value) > 0 && !isSettling.value);
const canRemove = computed(() => (session.value?.entries?.length ?? 0) > 1);
const entriesLabel = computed(() => {
    const count = session.value?.entries?.length ?? 0;
    return `${count} ${count === 1 ? trans('label.entry') : trans('label.entries')}`;
});

const loadSession = async () => {
    loading.value = true;
    const response = await foodflowSessionStore.show(props.sessionHash);
    session.value = response.data.session;
    loading.value = false;
};

onMounted(loadSession);

const selectCategory = (id) => {
    selectedCategoryId.value = id;
    focusWeightInput();
};

const addSessionEntry = async () => {
    if (!canAdd.value || submitting.value) {
        return;
    }
    submitting.value = true;
    try {
        const response = await addEntry(props.sessionHash, {
            food_category_id: selectedCategoryId.value,
            weight: parseFloat(weight.value),
        });
        session.value = response.data.session;
        selectedCategoryId.value = null;
        weight.value = '';
        focusWeightInput();
    } catch (error) {
        // Validation and server errors are surfaced centrally via the error store and toast interceptors.
    } finally {
        submitting.value = false;
    }
};

const removeSessionEntry = async (entryHash) => {
    if (!canRemove.value) {
        return;
    }
    try {
        const response = await removeEntry(props.sessionHash, entryHash);
        session.value = response.data.session;
    } catch (error) {
        // Validation and server errors are surfaced centrally via the error store and toast interceptors.
    }
};

const close = () => emit('close');
</script>

<template>
<div class="flex h-[calc(100vh-56px)] flex-col overflow-hidden md:h-[calc(100vh-100px)]">
    <div class="flex h-[52px] flex-shrink-0 items-center gap-3.5 border-b border-[#EDE8DF] bg-white px-4 sm:px-6">
        <button type="button" class="flex items-center gap-1.5 py-1.5 text-[13px] font-semibold text-[#375475] hover:text-[#4a6d93]" @click="close">
            <FontAwesomeIcon icon="arrow-left" />{{ $t('label.back') }}
        </button>
        <div class="h-5 w-px bg-[#EDE8DF]" />
        <template v-if="session">
            <span class="text-[14px] font-semibold text-[#1e2d3d]">{{ session.contact?.name }}</span>
            <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
            <span class="text-[12px] text-[#64748b]">· {{ formatDateTime(session.session_date) }}</span>
        </template>
    </div>

    <div v-if="loading" class="py-16 text-center text-grey-400">
        {{ $t('label.loading') }}
    </div>

    <div v-else-if="session" class="grid flex-1 grid-cols-1 overflow-y-auto md:grid-cols-[340px_1fr] md:overflow-hidden">
        <div class="flex flex-col gap-4 border-b border-[#EDE8DF] p-4 sm:p-5 md:overflow-y-auto md:border-b-0 md:border-r">
            <div class="rounded-xl bg-[#F7F4EF] p-4">
                <div class="flex items-center justify-between py-[5px] text-[13px]">
                    <span class="text-[#64748b]">{{ $t('label.type') }}</span>
                    <span class="font-semibold text-[#1e2d3d]">
                        <FoodFlowBadge :variant="isIntake ? 'green' : 'red'" dot>
                            {{ isIntake ? $t('label.intake') : $t('label.outtake') }}
                        </FoodFlowBadge>
                    </span>
                </div>
                <div class="flex items-center justify-between py-[5px] text-[13px]">
                    <span class="text-[#64748b]">{{ $t('label.date') }}</span>
                    <span class="font-semibold text-[#1e2d3d]">{{ formatDateTime(session.session_date) }}</span>
                </div>
                <div v-if="session.notes" class="session-detail-notes">
                    <span class="text-[#64748b]">{{ $t('label.notes') }}</span>
                    <p class="session-detail-notes-text">{{ session.notes }}</p>
                </div>
            </div>

            <div class="flex flex-col gap-3">
                <div>
                    <div class="mb-2 flex items-center justify-between">
                        <span class="text-[11px] font-bold uppercase tracking-[0.1em] text-[#375475]">{{ $t('label.add_entry') }}</span>
                        <ScaleConnectionStatus />
                    </div>
                    <div class="grid grid-cols-4 gap-1.5">
                        <div
                            v-for="category in foodCategoryOptions"
                            :key="category.id"
                            class="cursor-pointer rounded-lg border-2 bg-white p-2 text-center transition-all"
                            :class="selectedCategoryId === category.id ? 'border-[#375475] bg-[#e7edf4]' : 'border-[#EDE8DF] hover:border-[#375475]'"
                            @click="selectCategory(category.id)">
                            <div class="mb-0.5 text-[16px]">{{ categoryIcon(category.name) }}</div>
                            <div class="text-[10px] font-semibold leading-tight text-[#1e2d3d]">{{ category.name }}</div>
                        </div>
                    </div>
                </div>
                <div>
                    <div class="mb-1.5 text-[11px] font-bold uppercase tracking-[0.1em] text-[#375475]">{{ $t('label.weight') }}</div>
                    <div class="flex gap-2">
                        <div class="relative flex-1">
                            <!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
                            <input
                                ref="weightInput"
                                v-model="weight"
                                type="number"
                                placeholder="0.0"
                                min="0"
                                step="0.1"
                                inputmode="decimal"
                                class="w-full rounded-[10px] border-2 border-[#EDE8DF] bg-white py-3 pl-3.5 pr-11 text-[22px] font-semibold text-[#1e2d3d] outline-none focus:border-[#375475] focus:shadow-[0_0_0_3px_rgba(55,84,117,0.1)]">
                            <!-- eslint-enable @intlify/vue-i18n/no-raw-text -->
                            <span class="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[13px] font-semibold text-[#94a3b8]">{{ $t('label.lbs') }}</span>
                        </div>
                        <button
                            type="button"
                            class="rounded-[10px] bg-[#375475] px-[18px] text-[14px] font-bold text-white hover:bg-[#4a6d93] disabled:cursor-not-allowed disabled:bg-[#EDE8DF] disabled:text-[#94a3b8]"
                            :disabled="!canAdd || submitting"
                            @click="addSessionEntry">{{ $t('label.add') }}</button>
                    </div>
                </div>
            </div>
        </div>

        <div class="flex flex-col p-4 sm:p-5 md:overflow-hidden">
            <div class="mb-3 flex flex-shrink-0 items-center justify-between">
                <span class="text-[13px] font-bold uppercase tracking-[0.08em] text-[#375475]">{{ $t('label.entries') }}</span>
                <span class="text-[12px] font-semibold text-[#64748b]">{{ entriesLabel }}</span>
            </div>
            <div class="flex-1 md:overflow-y-auto">
                <div v-if="session.entries.length === 0" class="rounded-[10px] border border-dashed border-[#EDE8DF] bg-white p-5 text-center text-[13px] text-[#94a3b8]">
                    {{ $t('label.no_entries_yet') }}
                </div>
                <div v-for="entry in session.entries" :key="entry.hash" class="mb-1.5 flex items-center justify-between rounded-[10px] border border-[#EDE8DF] bg-white px-3.5 py-3">
                    <div class="flex items-center gap-2.5">
                        <span class="text-[20px]">{{ categoryIcon(entry.food_category?.name) }}</span>
                        <span class="text-[13px] font-semibold">{{ entry.food_category?.name }}</span>
                    </div>
                    <div class="flex items-center gap-2.5">
                        <span class="text-[15px] font-bold" :class="weightColorClass">
                            {{ weightSign }}{{ parseFloat(entry.weight).toFixed(1) }} {{ $t('label.lbs') }}
                        </span>
                        <!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
                        <button
                            v-if="canRemove"
                            type="button"
                            class="rounded-md p-1 text-[16px] text-[#94a3b8] hover:bg-[#fde8e7] hover:text-[#F06660]"
                            :title="$t('label.remove')"
                            @click="removeSessionEntry(entry.hash)">✕</button>
                        <!-- eslint-enable @intlify/vue-i18n/no-raw-text -->
                    </div>
                </div>
                <div v-if="session.entries.length" class="mt-2 flex flex-shrink-0 items-center justify-between rounded-[10px] border border-[#EDE8DF] bg-[#F7F4EF] px-3.5 py-2.5">
                    <span class="text-[12px] font-bold uppercase tracking-[0.08em] text-[#375475]">{{ $t('label.total') }}</span>
                    <span class="text-[16px] font-bold" :class="weightColorClass">{{ weightSign }}{{ totalWeight.toFixed(1) }} {{ $t('label.lbs') }}</span>
                </div>
            </div>
        </div>
    </div>
</div>
</template>
